import React, { FC, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik, useFormikContext } from 'formik';
import { format } from 'date-fns';

import {
  PURCHASE_ORDER_STATUS_TO_ACTION,
  PURCHASE_ORDER_STATUS_TO_LABEL,
} from 'constants/purchaseOrder';
import CreatePurchaseOrderForm from 'components/modules/CreatePurchaseOrderModal/CreatePurchaseOrderForm';
import {
  CreatePurchaseOrderFormValues,
  formatPurchaseOrderDate,
  isPurchaseOrderFormValid,
  mapPurchaseOrderToFormValues,
} from 'components/modules/CreatePurchaseOrderModal/CreatePurchaseOrderModal.utils';

import FormikInput from 'components/primitives/FormikInput';
import { useIsSuperAdmin } from 'store/slices/auth/authSelectors';
import {
  formatManufacturerEmails,
  getPurchaseOrderManufacturerEmails,
} from 'utils/manufacturer';

import { PurchaseOrderDetailsModalViewProps } from './PurchaseOrderDetailsModal.props';
import DeletePurchaseOrderDialog from './DeletePurchaseOrderDialog';
import SendPurchaseOrderDialog from './SendPurchaseOrderDialog';

const EDIT_FORM_ID = 'edit-purchase-order-form';

const Field = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => (
  <Box mb={3}>
    <Text fontSize="12px" color="#afafaf" mb={1}>
      {label}
    </Text>
    <Text>{value === undefined || value === '' ? '-' : value}</Text>
  </Box>
);

const formatLastUpdate = (value?: string) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, 'MMMM d, yyyy');
};

const DeletePurchaseOrderButton: FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <Button
    bg="#E53E3E"
    color="#fff"
    _hover={{ bg: '#C53030' }}
    _active={{ bg: '#9B2C2C' }}
    onClick={onClick}>
    Delete
  </Button>
);

type EditFooterProps = {
  onViewAttachments: () => void;
  onSendClick: () => void;
  onDeleteClick: () => void;
  isSavingPurchaseOrder: boolean;
};

const EditPurchaseOrderFooter: FC<EditFooterProps> = ({
  onViewAttachments,
  onSendClick,
  onDeleteClick,
  isSavingPurchaseOrder,
}) => {
  const toast = useToast();
  const isSuperAdmin = useIsSuperAdmin();
  const {
    values,
    dirty,
    isSubmitting,
  } = useFormikContext<CreatePurchaseOrderFormValues>();

  return (
    <Flex mt={6} align="center" flexShrink={0} gap={3}>
      <Flex flex={1} />
      <Flex justify="center" gap={3} wrap="wrap">
        <Button variant="outline" onClick={onViewAttachments}>
          View Attachments
        </Button>
        <Button
          type="submit"
          form={EDIT_FORM_ID}
          isDisabled={!isPurchaseOrderFormValid(values)}
          isLoading={isSubmitting || isSavingPurchaseOrder}>
          Save
        </Button>
        {isSuperAdmin && (
          <Button
            onClick={() => {
              if (dirty) {
                toast({
                  status: 'warning',
                  title: 'Save your changes before sending',
                  isClosable: true,
                  duration: 4000,
                });
                return;
              }

              onSendClick();
            }}>
            Send
          </Button>
        )}
      </Flex>
      <Flex flex={1} justify="flex-end">
        {isSuperAdmin && <DeletePurchaseOrderButton onClick={onDeleteClick} />}
      </Flex>
    </Flex>
  );
};

const PurchaseOrderDetailsModalView: FC<PurchaseOrderDetailsModalViewProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  onViewAttachments,
  manufacturers,
  washHubs,
  arts,
  skus,
  washHubAdmins,
  isLoadingManufacturers,
  isLoadingWashHubs,
  isLoadingArts,
  isLoadingSkus,
  isLoadingWashHubAdmins,
  onDestinationHubChange,
  onSavePurchaseOrder,
  isSavingPurchaseOrder,
  onSendPurchaseOrder,
  isSendingPurchaseOrder,
  onUpdatePurchaseOrderCosts,
  isUpdatingPurchaseOrderCosts,
  onDeletePurchaseOrder,
  isDeletingPurchaseOrder,
  onMarkAsReceived,
  onPayVendor,
}) => {
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isSuperAdmin = useIsSuperAdmin();
  const isOpenStatus = purchaseOrder?.status === 'open';
  const isSentStatus = purchaseOrder?.status === 'sent';
  const canEditCosts = purchaseOrder?.status !== 'paid';
  const statusLabel =
    PURCHASE_ORDER_STATUS_TO_LABEL[
      purchaseOrder?.status as keyof typeof PURCHASE_ORDER_STATUS_TO_LABEL
    ] || purchaseOrder?.status;
  const actionLabel =
    PURCHASE_ORDER_STATUS_TO_ACTION[
      purchaseOrder?.status as keyof typeof PURCHASE_ORDER_STATUS_TO_ACTION
    ];

  const manufacturer = manufacturers.find(
    (item) => item._id === purchaseOrder?.manufacturerId,
  );
  const location = washHubs.find(
    (item) => item._id === purchaseOrder?.destinationLocationId,
  );
  const manufacturerEmails = getPurchaseOrderManufacturerEmails(
    purchaseOrder,
    manufacturer?.email,
  );
  const isFormReady =
    !isLoadingManufacturers &&
    !isLoadingWashHubs &&
    !isLoadingArts &&
    (!purchaseOrder?.manufacturerId || !isLoadingSkus) &&
    (!purchaseOrder?.destinationLocationId || !isLoadingWashHubAdmins);

  const initialValues = useMemo(() => {
    if (!purchaseOrder) {
      return undefined;
    }

    return mapPurchaseOrderToFormValues(purchaseOrder, {
      manufacturer,
      location,
      skus,
      arts,
      washHubAdmins,
    });
  }, [purchaseOrder, manufacturer, location, skus, arts, washHubAdmins]);

  useEffect(() => {
    if (!isOpen) {
      setIsSendDialogOpen(false);
      setIsDeleteDialogOpen(false);
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        size={isOpenStatus ? '6xl' : '3xl'}
        isCentered
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior={isOpenStatus ? 'outside' : 'inside'}>
        <ModalOverlay />
        <ModalContent
          borderRadius="10px"
          maxW={isOpenStatus ? '1200px' : undefined}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden">
          <ModalCloseButton _focus={{ outline: 'none' }} />
          <ModalHeader mt={4} textAlign="center" flexShrink={0}>
            Purchase Order
            {purchaseOrder?.poNumer ? ` ${purchaseOrder.poNumer}` : ''}
          </ModalHeader>
          <ModalBody
            pb={8}
            flex="1"
            display="flex"
            flexDirection="column"
            overflow={isOpenStatus ? 'hidden' : 'auto'}
            minH={0}>
            {isOpenStatus ? (
              isFormReady && initialValues ? (
                <Formik
                  key={purchaseOrder?._id}
                  initialValues={initialValues}
                  onSubmit={async (values, helpers) => {
                    await onSavePurchaseOrder(values);
                    helpers.resetForm({ values });
                  }}>
                  <Flex direction="column" flex="1" minH={0} overflow="hidden">
                    <CreatePurchaseOrderForm
                      formId={EDIT_FORM_ID}
                      isEditing
                      manufacturers={manufacturers}
                      washHubs={washHubs}
                      arts={arts}
                      washHubAdmins={washHubAdmins}
                      isLoadingManufacturers={isLoadingManufacturers}
                      isLoadingWashHubs={isLoadingWashHubs}
                      isLoadingArts={isLoadingArts}
                      isLoadingWashHubAdmins={isLoadingWashHubAdmins}
                      onDestinationHubChange={onDestinationHubChange}
                    />
                    <EditPurchaseOrderFooter
                      onViewAttachments={onViewAttachments}
                      onSendClick={() => setIsSendDialogOpen(true)}
                      onDeleteClick={() => setIsDeleteDialogOpen(true)}
                      isSavingPurchaseOrder={isSavingPurchaseOrder}
                    />
                  </Flex>
                </Formik>
              ) : (
                <Center flex="1" minH="320px">
                  <Spinner />
                </Center>
              )
            ) : (
              <>
                <Flex gap={6} wrap="wrap">
                  <Box flex="1" minW="220px">
                    <Field label="PO#" value={purchaseOrder?.poNumer} />
                    <Field
                      label="Manufacturer"
                      value={
                        manufacturer?.name || purchaseOrder?.manufacturer?.name
                      }
                    />
                    <Field
                      label="Manufacturer Contact"
                      value={formatManufacturerEmails(manufacturerEmails)}
                    />
                    <Field
                      label="Destination Hub"
                      value={purchaseOrder?.location}
                    />
                    <Field label="Status" value={statusLabel} />
                  </Box>
                  <Box flex="1" minW="220px">
                    <Field
                      label="Date"
                      value={formatPurchaseOrderDate(
                        purchaseOrder?.date || purchaseOrder?.createdAt,
                      )}
                    />
                    <Field
                      label="Last Update"
                      value={formatLastUpdate(purchaseOrder?.updatedAt)}
                    />
                    <Field
                      label="Liftgate Required"
                      value={purchaseOrder?.liftGateRequired ? 'Yes' : 'No'}
                    />
                    <Field
                      label="Shipping Notes"
                      value={purchaseOrder?.shippingNotes}
                    />
                    <Field
                      label="Receiving Notes"
                      value={purchaseOrder?.receivingNotes}
                    />
                  </Box>
                </Flex>

                {canEditCosts ? (
                  <Formik
                    enableReinitialize
                    initialValues={{
                      shippingCost: purchaseOrder?.shippingCost ?? 0,
                      tax: purchaseOrder?.tax ?? 0,
                      costAdjustment: purchaseOrder?.costAdjustment ?? 0,
                    }}
                    onSubmit={async (values, helpers) => {
                      await onUpdatePurchaseOrderCosts({
                        shippingCost: Number(values.shippingCost) || 0,
                        tax: Number(values.tax) || 0,
                        costAdjustment: Number(values.costAdjustment) || 0,
                      });
                      helpers.resetForm({ values });
                    }}>
                    {({ isSubmitting }) => (
                      <Form>
                        <Box mt={4}>
                          <Text fontSize="12px" color="#afafaf" mb={2}>
                            Costs
                          </Text>
                          <Flex gap={3}>
                            <FormikInput
                              name="shippingCost"
                              type="number"
                              label="Shipping Cost"
                            />
                            <FormikInput name="tax" type="number" label="Tax" />
                            <FormikInput
                              name="costAdjustment"
                              type="number"
                              label="Cost Adjustment"
                            />
                          </Flex>
                          <Button
                            mt={3}
                            type="submit"
                            isLoading={
                              isSubmitting || isUpdatingPurchaseOrderCosts
                            }>
                            Save Costs
                          </Button>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <Box mt={4}>
                    <Field
                      label="Shipping Cost"
                      value={purchaseOrder?.shippingCost ?? 0}
                    />
                    <Field label="Tax" value={purchaseOrder?.tax ?? 0} />
                    <Field
                      label="Cost Adjustment"
                      value={purchaseOrder?.costAdjustment ?? 0}
                    />
                  </Box>
                )}

                <Box mt={4}>
                  <Text fontSize="12px" color="#afafaf" mb={2}>
                    Order Details
                  </Text>
                  {purchaseOrder?.orderDetails?.length ? (
                    purchaseOrder.orderDetails.map((orderDetail, index) => (
                      <Text key={`${orderDetail.skuId}-${index}`} mb={1}>
                        {orderDetail.itemQuantity} items (
                        {orderDetail.boxQuantity} boxes) x{' '}
                        {orderDetail.art?.number} {orderDetail.sku?.name}
                      </Text>
                    ))
                  ) : (
                    <Text>-</Text>
                  )}
                </Box>

                <Flex mt={8} align="center" gap={3}>
                  <Flex flex={1} />
                  <Flex justify="center" gap={3} wrap="wrap">
                    <Button variant="outline" onClick={onViewAttachments}>
                      View Attachments
                    </Button>
                    {actionLabel ? (
                      <Button
                        onClick={() => {
                          if (
                            purchaseOrder?.status === 'sent' ||
                            purchaseOrder?.status === 'partiallyReceived'
                          ) {
                            onMarkAsReceived?.();
                          }

                          if (purchaseOrder?.status === 'received') {
                            onPayVendor?.();
                          }
                        }}>
                        {actionLabel}
                      </Button>
                    ) : null}
                  </Flex>
                  <Flex flex={1} justify="flex-end">
                    {isSuperAdmin && isSentStatus && (
                      <DeletePurchaseOrderButton
                        onClick={() => setIsDeleteDialogOpen(true)}
                      />
                    )}
                  </Flex>
                </Flex>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <SendPurchaseOrderDialog
        isOpen={isSendDialogOpen}
        purchaseOrder={purchaseOrder}
        manufacturerName={
          manufacturer?.name || purchaseOrder?.manufacturer?.name
        }
        defaultManufacturerEmails={manufacturerEmails}
        washHubName={location?.name || purchaseOrder?.location}
        defaultWashHubEmail={location?.email}
        isSending={isSendingPurchaseOrder}
        onClose={() => setIsSendDialogOpen(false)}
        onSend={async (payload) => {
          await onSendPurchaseOrder(payload);
          setIsSendDialogOpen(false);
        }}
      />
      <DeletePurchaseOrderDialog
        isOpen={isDeleteDialogOpen}
        poNumber={purchaseOrder?.poNumer}
        isDeleting={isDeletingPurchaseOrder}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={async (reasonForDeletion) => {
          await onDeletePurchaseOrder(reasonForDeletion);
          setIsDeleteDialogOpen(false);
        }}
      />
    </>
  );
};

export default PurchaseOrderDetailsModalView;
